import { useApp } from '../../context/AppContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { getInvestmentTypeLabels, TAX_RULES, INVESTMENT_TYPES } from '../../utils/taxEngine';
import { Info, Calculator, Percent } from 'lucide-react';

export const InvestmentTypeSelector = () => {
  const { inputs, updateInput, t, language, investmentType } = useApp();
  const investmentTypes = getInvestmentTypeLabels(language);
  const currentType = investmentTypes.find(type => type.value === investmentType);
  const taxRule = TAX_RULES[investmentType];
  const isAccumulation = investmentType === INVESTMENT_TYPES.ACCUMULATION_PLAN;
  const isChildFuture = investmentType === INVESTMENT_TYPES.CHILD_FUTURE;
  const isSavings = investmentType === INVESTMENT_TYPES.SAVINGS_ACCOUNT;
  const isCredit = investmentType === INVESTMENT_TYPES.CREDIT;
  const showAdminFee = isAccumulation || isChildFuture;

  const handleTypeChange = (value) => {
    // Just switch type — each type remembers its own settings
    updateInput('investmentType', value);
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-foreground/70 dark:text-foreground/80 font-semibold">
            {t('investmentType')}
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-foreground/50 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs bg-popover text-popover-foreground border shadow-lg">
                <p className="text-sm">{t('tooltipInvestmentType')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <Select value={investmentType} onValueChange={handleTypeChange}>
          <SelectTrigger className="w-full h-12 bg-background border-2 focus:ring-2 focus:ring-blue-500/50 rounded-xl">
            <SelectValue placeholder={t('investmentType')} />
          </SelectTrigger>
          <SelectContent className="bg-popover border shadow-xl max-h-80">
            {investmentTypes.map((type) => (
              <SelectItem key={type.value} value={type.value} className="cursor-pointer py-4">
                <div className="flex flex-col gap-1">
                  <span className="font-semibold text-base">{type.icon} {type.label}</span>
                  <span className="text-xs text-muted-foreground">{type.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Badges */}
      {currentType && !isCredit && (
        <div className="flex flex-wrap items-center gap-2">
          {isSavings && (
            <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 py-1.5 px-3">
              <Calculator className="h-3.5 w-3.5 mr-1.5" />{t('simpleInterest')}
            </Badge>
          )}
          {(isAccumulation || isChildFuture) && (
            <Badge variant="outline" className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border-emerald-300 py-1.5 px-3">
              <Calculator className="h-3.5 w-3.5 mr-1.5" />{t('compoundInterest')}
            </Badge>
          )}
          {taxRule?.interestTaxRate > 0 && (
            <Badge variant="outline" className="text-muted-foreground py-1.5 px-3">
              {language === 'ro' ? `Impozit ${taxRule.interestTaxRate}%` : `${taxRule.interestTaxRate}% tax`}
            </Badge>
          )}
        </div>
      )}

      {/* Admin fee */}
      {showAdminFee && (
        <div className="p-5 rounded-2xl bg-purple-100 dark:bg-purple-900/40 border-2 border-purple-200 dark:border-purple-800 space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-purple-700 dark:text-purple-300 font-semibold">
              {t('adminFeePercent')}
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-purple-500 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-sm">{t('tooltipAdminFee')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              value={inputs.adminFeeRate ?? 1.5}
              onChange={(e) => updateInput('adminFeeRate', parseFloat(e.target.value) || 0)}
              className="w-24 text-right font-semibold h-10 text-base bg-white dark:bg-purple-950"
              step="0.1" min="0" max="5"
            />
            <Percent className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <span className="text-sm text-purple-700 dark:text-purple-300">
              {language === 'ro' ? 'pe an' : 'per year'}
            </span>
          </div>
        </div>
      )}

      {/* Tax checkbox */}
      {!isCredit && (
        <div className="flex items-center space-x-2 pt-1">
          <Checkbox
            id="includeTaxes"
            checked={inputs.includeTaxes ?? false}
            onCheckedChange={(checked) => updateInput('includeTaxes', checked)}
          />
          <Label htmlFor="includeTaxes" className="text-sm font-medium cursor-pointer">
            {t('includeTaxes')}
          </Label>
        </div>
      )}
    </div>
  );
};

export default InvestmentTypeSelector;
